// Import React
import React from "react";



// Create the icon used for each journey selector
const JourneySelectorIcon = ({
    iconType,
}) => {

    // Display the website icon
    if (iconType === "website") {

        // Return the website icon
        return (

            // Create the browser window SVG
            <svg
                className="how-it-works__journey-icon-svg"
                viewBox="0 0 64 64"
                aria-hidden="true"
            >

                {/* Draw the browser window */}
                <rect
                    x="8"
                    y="11"
                    width="48"
                    height="42"
                    rx="4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                />

                {/* Draw the browser toolbar */}
                <path
                    d="M8 21 H56"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                />

                {/* Draw the first browser dot */}
                <circle
                    cx="15"
                    cy="16"
                    r="1.7"
                    fill="currentColor"
                />

                {/* Draw the second browser dot */}
                <circle
                    cx="21"
                    cy="16"
                    r="1.7"
                    fill="currentColor"
                />

                {/* Draw the website image block */}
                <rect
                    x="14"
                    y="28"
                    width="16"
                    height="16"
                    rx="2"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                />

                {/* Draw the website text lines */}
                <path
                    d="M35 29 H49 M35 35 H46 M35 41 H50"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                />

            </svg>

        );

    }



    // Display the audience icon
    if (iconType === "audience") {

        // Return the audience icon
        return (

            // Create the audience SVG
            <svg
                className="how-it-works__journey-icon-svg"
                viewBox="0 0 64 64"
                aria-hidden="true"
            >

                {/* Draw the first person's head */}
                <circle
                    cx="22"
                    cy="22"
                    r="7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                />

                {/* Draw the second person's head */}
                <circle
                    cx="43"
                    cy="25"
                    r="6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                />

                {/* Draw the first person's body */}
                <path
                    d="M10 48 C12 37 17 33 24 33 C30 33 35 37 37 44"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                />

                {/* Draw the second person's body */}
                <path
                    d="M38 38 C41 35 44 34 48 35 C54 36 57 41 58 48"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                />

                {/* Draw the sharing arrow */}
                <path
                    d="M23 50 H46 M40 44 L46 50 L40 56"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />

            </svg>

        );

    }



    // Return the idea icon
    return (

        // Create the lightbulb SVG
        <svg
            className="how-it-works__journey-icon-svg"
            viewBox="0 0 64 64"
            aria-hidden="true"
        >

            {/* Draw the bulb */}
            <path
                d="M20 27 C20 18 25 12 32 12 C39 12 44 18 44 27 C44 33 41 36 38 40 C36 42 36 44 36 46 H28 C28 44 28 42 26 40 C23 36 20 33 20 27 Z"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinejoin="round"
            />

            {/* Draw the bulb base */}
            <path
                d="M27 50 H37 M29 55 H35"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
            />

            {/* Draw the first idea ray */}
            <path
                d="M32 5 V1"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
            />

            {/* Draw the second idea ray */}
            <path
                d="M15 12 L11 8"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
            />

            {/* Draw the third idea ray */}
            <path
                d="M49 12 L53 8"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
            />

        </svg>

    );

};



// Export the reusable How It Works component
export const HowItWorks = ({
    sectionRef,
    activeJourney,
    onJourneyChange,
    journeys,
}) => {



    // Get the currently selected journey data
    const selectedJourney = journeys?.[activeJourney];



    // Scroll smoothly to the requested section when it is not already fully visible.
    const handleScrollToClick = (scrollToElementID) => {

        // Find the requested section in the page.
        const goToSection = document.getElementById(scrollToElementID);

        // Stop when the requested section cannot be found.
        if (!goToSection) {

            // Exit the handler.
            return;

        }

        // Get the element's current position relative to the viewport.
        const elementRect = goToSection.getBoundingClientRect();

        // Get the current visible viewport height.
        const viewportHeight = window.innerHeight;

        // Check whether the top of the element is currently visible.
        const isTopInView = elementRect.top >= 0;

        // Check whether the bottom of the element is currently visible.
        const isBottomInView = elementRect.bottom <= viewportHeight;

        // Check whether the complete element is already visible.
        const isFullyInView = isTopInView && isBottomInView;

        // Do not scroll when the complete element is already visible.
        if (isFullyInView) {

            // Exit without changing the user's scroll position.
            return;

        }

        // Scroll the element into view.
        goToSection.scrollIntoView({

            // Animate the movement.
            behavior: "smooth",

            // Align the bottom of the element with the bottom of the viewport.
            block: "end",

        });

    };



    // Return nothing if the selected journey cannot be found
    if (!selectedJourney) {

        // Return nothing safely
        return null;

    }



    // Render the complete How It Works section
    return (

        // Create the main How It Works section
        <section
            ref={sectionRef}
            className="how-it-works"
            id="how-it-works"
        >

            {/* Create the inner width-limited container */}
            <div className="how-it-works__inner">

                {/* Display the main section heading */}
                <h2 className="how-it-works__title">

                    {/* Display the heading text */}
                    How can StrichSuite make you money?

                </h2>



                {/* Display the introductory selector text */}
                <p className="how-it-works__intro">

                    {/* Prompt the visitor to choose the route that suits them */}
                    Pick the one that sounds most like you.

                </p>



                {/* Display the journey selector cards */}
                <div className="how-it-works__journey-selector">

                    {/* Loop through every selectable journey */}
                    {Object.entries(journeys).map(([journeyKey, journey]) => {

                        // Work out whether this journey is currently selected
                        const isActive = journeyKey === activeJourney;

                        // Return the journey selector button
                        return (

                            // Create the selector card
                            <button
                                key={journeyKey}
                                type="button"
                                className={[
                                    // Always include the base card class
                                    "how-it-works__journey-button",

                                    // Add the active modifier when selected
                                    isActive
                                        ? "how-it-works__journey-button--active"
                                        : "",
                                ].filter(Boolean).join(" ")}
                                onClick={() => {
                                    onJourneyChange(journeyKey);
                                    handleScrollToClick("journey-flow");
                                }}
                                aria-pressed={isActive}
                            >

                                {/* Display the journey icon */}
                                <span className="how-it-works__journey-icon">

                                    {/* Render the relevant selector icon */}
                                    <JourneySelectorIcon
                                        iconType={journey.selectorIcon}
                                    />

                                </span>



                                {/* Create the selector content area */}
                                <span className="how-it-works__journey-content">

                                    {/* Display the main selector title */}
                                    <span className="how-it-works__journey-title">

                                        {/* Display the journey title */}
                                        {journey.selectorTitle}

                                    </span>



                                    {/* Display the optional playful subtitle */}
                                    {journey.selectorSubtitle && (

                                        // Create the selector subtitle
                                        <span className="how-it-works__journey-subtitle">

                                            {/* Display the subtitle */}
                                            {journey.selectorSubtitle}

                                        </span>

                                    )}



                                    {/* Display the selector description */}
                                    <span className="how-it-works__journey-description">

                                        {/* Display the short journey description */}
                                        {journey.selectorDescription}

                                    </span>

                                </span>



                                {/* Display the active indicator */}
                                <span
                                    className="how-it-works__journey-indicator"
                                    aria-hidden="true"
                                >

                                    {/* Display a tick when active and an arrow when inactive */}
                                    {isActive ? "✓" : "→"}

                                </span>

                            </button>

                        );

                    })}

                </div>



                {/* Create the selected journey details area */}
                <div
                    id="journey-flow"
                    key={activeJourney}
                    className="how-it-works__journey"
                >

                    {/* Display the journey-specific subtitle */}
                    <p className="how-it-works__subtitle">

                        {/* Display the selected subtitle */}
                        {selectedJourney.subtitle}

                    </p>



                    {/* Display the journey description 
                    <p className="how-it-works__description">

                        {/* Display the selected description 
                        {selectedJourney.description}

                    </p>
                    */}


                    {/* Display the individual journey steps */}
                    <div
                        className="how-it-works__steps"
                        style={{
                            // Tell CSS how many steps are currently displayed
                            "--how-it-works-step-count": selectedJourney.steps.length,
                        }}
                    >

                        {/* Loop through each journey step */}
                        {selectedJourney.steps.map((step, stepIndex) => {

                            // Return the individual journey step
                            return (

                                // Create a wrapper for each step and its optional arrow
                                <React.Fragment key={step.key}>

                                    {/* Create the journey step */}
                                    <article
                                        className={[
                                            // Always include the base step class
                                            "how-it-works__step",

                                            // Centre the final step when the journey has an odd number of steps
                                            selectedJourney.steps.length % 2 !== 0 &&
                                            stepIndex === selectedJourney.steps.length - 1
                                                ? "how-it-works__step--odd-last"
                                                : "",
                                        ].filter(Boolean).join(" ")}
                                    >

                                        {/* Display the step image */}
                                        <div className="how-it-works__image-wrapper">

                                            {/* Render the supplied step illustration */}
                                            <img
                                                className="how-it-works__image"
                                                src={step.image}
                                                alt={step.imageAlt}
                                            />

                                        </div>



                                        {/* Display the step caption */}
                                        <p className="how-it-works__step-text">

                                            {/* Display the step number */}
                                            <span className="how-it-works__step-number">

                                                {/* Display the one-based step number */}
                                                {stepIndex + 1}.

                                            </span>

                                            {/* Add spacing after the step number */}
                                            {" "}

                                            {/* Display the step description */}
                                            {step.text}

                                        </p>

                                    </article>



                                    {/* Display an arrow between steps except after the final step */}
                                    {stepIndex < selectedJourney.steps.length - 1 && (

                                        // Create the connecting arrow
                                        <div
                                            className="how-it-works__arrow"
                                            aria-hidden="true"
                                        >

                                            {/* Display a simple arrow symbol */}
                                            →

                                        </div>

                                    )}

                                </React.Fragment>

                            );

                        })}

                    </div>



                    {/* Display the journey-specific secondary action when supplied */}
                    {selectedJourney.secondaryAction && (

                        // Create the secondary action wrapper
                        <div className="how-it-works__secondary-action">

                            {/* Display optional supporting text */}
                            {selectedJourney.secondaryAction.caption && (

                                // Create the caption
                                <p className="how-it-works__secondary-caption">

                                    {/* Display the caption */}
                                    {selectedJourney.secondaryAction.caption}

                                </p>

                            )}

                            {/* Display the secondary action link */}
                            <a
                                className="how-it-works__secondary-button"
                                href={selectedJourney.secondaryAction.href}
                                target={
                                    selectedJourney.secondaryAction.openNewTab
                                        ? "_blank"
                                        : undefined
                                }
                                rel={
                                    selectedJourney.secondaryAction.openNewTab
                                        ? "noreferrer"
                                        : undefined
                                }
                            >

                                {/* Display the action button text */}
                                {selectedJourney.secondaryAction.label}

                            </a>

                        </div>

                    )}

                </div>

            </div>

        </section>

    );

};